import { type FC, type FocusEventHandler, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { FormInstance, FormItemProps } from 'antd';
import i18n from 'i18next';

import { ReactComponent as ZoomIcon } from '@/assets/icons/zoom-input.svg';

import { setAppLoader } from '../../../redux/common/CommonActions';
import { getZipCodeDetailsEffect } from '../../../redux/shipment/ShipmentEffects';
import PostalSearchModal from '../../screens/common/PostalSearchModal/PostalSearchModal';
import type { PostalInfo } from '../../screens/common/types';
import IconButton from '../Buttons/IconButton';
import SvgConverter from '../Common/SvgConverter';
import FormInput from '../Inputs/FormInput';

export interface PostalCodeInputValues {
  state: string;
  city: string;
  countryCode: string;
  zip: string;
}

interface PostalCodeInputProps {
  placeholder?: string;
  label?: string;
  rules?: FormItemProps['rules'];
  name?: string;
  editMode: boolean;
  countryCode?: string;
  handleZipDetails?: (values: PostalCodeInputValues) => void;
  margin?: string;
  mode?: string;
  disableValidation?: boolean;
  maxLength?: number;
  tabIndex?: number;
  labelForTestId?: string;
  form?: FormInstance;
}

const PostalCodeInput: FC<PostalCodeInputProps> = ({
  placeholder = i18n.t('translation:default.fields.postalCode.placeholder'),
  label = i18n.t('translation:default.fields.postalCode.label'),
  rules = [],
  name,
  editMode,
  countryCode,
  handleZipDetails,
  margin,
  mode = '',
  disableValidation = false,
  maxLength,
  tabIndex,
  labelForTestId,
  form,
}) => {
  const [modal, setModal] = useState(false);
  const dispatch = useDispatch();
  // Make request only when previous value is different
  const [prevValue, setPrevValue] = useState<string | null>();

  useEffect(() => {
    if (editMode) {
      setPrevValue(form?.getFieldValue(name));
    } else {
      setPrevValue(null);
    }
  }, [editMode, form, name]);

  const handleBlur: FocusEventHandler<HTMLInputElement> = async ({ target: { value } }) => {
    if (!countryCode || !value || !handleZipDetails || prevValue === value) {
      return;
    }

    try {
      dispatch(setAppLoader(true));
      const payload = { CountryCode: countryCode, Zip: value };
      const { data } = await getZipCodeDetailsEffect(payload);
      dispatch(setAppLoader(false));
      handleZipDetails(data);
      setPrevValue(data?.zip);
    } catch {
      dispatch(setAppLoader(false));
    }
  };

  const handleSelect = (data: PostalInfo) => {
    handleZipDetails?.({
      city: data.city.trim(),
      countryCode: data.country.trim(),
      state: data.state.trim(),
      zip: data.zip.trim(),
    });
    setModal(false);
    setPrevValue(data.zip.trim());
  };

  return (
    <>
      <FormInput
        placeholder={placeholder}
        label={label}
        margin={margin}
        rules={rules}
        name={name}
        editMode={editMode}
        onBlur={handleBlur}
        mode={mode}
        addonAfter={
          <IconButton onClick={() => setModal(true)}>
            <SvgConverter Icon={ZoomIcon} color="muted" />
          </IconButton>
        }
        disableValidation={disableValidation}
        maxLength={maxLength}
        tabIndex={tabIndex}
      />
      <PostalSearchModal
        open={modal}
        countryCode={countryCode}
        handleSelect={handleSelect}
        handleClose={() => setModal(false)}
        labelForTestId={labelForTestId}
      />
    </>
  );
};

export default PostalCodeInput;
